import type { ExposureBias, SessionAnalytics } from '@/types/session';
import { DashboardPanel } from './DashboardPanel';
import { RailSkeleton } from './RailSkeleton';
import { RailStateBlock } from './RailStateBlock';
import {
  formatExposureSnapshotTime,
  formatPrice,
  formatSessionDateLabel,
  formatStrike,
  nonEmpty,
} from './dashboardFormat';
import type { RailPanelState } from './railPanelState.utils';

interface OpenCloseSnapshotPanelProps {
  state: RailPanelState;
  sessionDate: string;
  onRetry: () => void;
}

const HEAD_CLASS_NAME =
  'border-b border-line px-1 py-1 text-center text-xs font-normal leading-[1.35] text-soft first:text-left';
const CELL_CLASS_NAME =
  'border-b border-line px-1 py-[5px] text-center leading-[1.35] first:text-left [tr:last-child_&]:border-b-0';

export function OpenCloseSnapshotPanel({
  state,
  sessionDate,
  onRetry,
}: OpenCloseSnapshotPanelProps) {
  return (
    <DashboardPanel title="Open / Close Snapshot" subtitle={formatSessionDateLabel(sessionDate)}>
      {state.kind === 'loading' ? (
        <RailSkeleton className="mt-3.5 h-[110px]" />
      ) : state.kind === 'error' ? (
        <RailStateBlock
          tone="error"
          title="Open/close unavailable"
          message={state.message}
          onRetry={onRetry}
        />
      ) : (
        <>
          <table className="mt-1.5 w-full border-collapse text-[13px] text-fg">
            <thead>
              <tr>
                <th className={HEAD_CLASS_NAME}>Time</th>
                <th className={HEAD_CLASS_NAME}>Price</th>
                <th className={HEAD_CLASS_NAME}>Major GEX</th>
                <th className={HEAD_CLASS_NAME}>GEX Bias</th>
                <th className={HEAD_CLASS_NAME}>VEX Bias</th>
              </tr>
            </thead>
            <tbody>
              {state.analytics.openCloseSnapshot.map((row) => (
                <tr key={row.label}>
                  <td className={CELL_CLASS_NAME}>
                    <span>
                      {row.label} <span className="text-dim">{row.time}</span>
                    </span>
                    {row.exposureObservedAt != null && (
                      <span className="block text-[10px] text-dim">
                        Observed {formatExposureSnapshotTime(row.exposureObservedAt)}
                      </span>
                    )}
                  </td>
                  <td className={`${CELL_CLASS_NAME} font-mono`}>{formatPrice(row.price)}</td>
                  <td className={`${CELL_CLASS_NAME} font-mono`}>
                    {row.gexBias === 'Unavailable' ? (
                      <NotAvailable />
                    ) : (
                      formatStrike(row.nearestStrike)
                    )}
                  </td>
                  <td className={CELL_CLASS_NAME}>
                    <Bias bias={row.gexBias} />
                  </td>
                  <td className={CELL_CLASS_NAME}>
                    <Bias bias={row.vexBias} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11.5px] text-dim">{snapshotNote(state.analytics)}</p>
        </>
      )}
    </DashboardPanel>
  );
}

/**
 * Rows still render when the open/close summary fails (the server falls back to bars and
 * the live spot), so the reason is a note; otherwise disclose which exposure snapshot the
 * bias columns use.
 */
function snapshotNote({
  dataAvailability,
  exposureSource,
  exposureUpdatedAt,
  openCloseSnapshot,
}: SessionAnalytics): string {
  if (dataAvailability.openClose.status === 'unavailable') {
    return nonEmpty(dataAvailability.openClose.reason) ?? 'Open/close summary unavailable';
  }
  const observations = openCloseSnapshot
    .flatMap((row) =>
      row.exposureObservedAt == null
        ? []
        : [`${row.label} observed ${formatExposureSnapshotTime(row.exposureObservedAt)}`],
    )
    .join(' · ');
  if (exposureSource === 'saved') {
    return observations === ''
      ? 'Saved exposure observations are available for this session.'
      : `${observations}. “Observed” is the actual capture time, which may differ from the listed market time.`;
  }
  if (dataAvailability.exposure.status === 'available') {
    const openingObservation = openCloseSnapshot.find(
      (row) => row.label === 'Open',
    )?.exposureObservedAt;
    const openingNote =
      openingObservation == null
        ? 'Opening exposure will appear after its observation is saved.'
        : `Opening exposure observed ${formatExposureSnapshotTime(openingObservation)}.`;
    return `${openingNote} Last uses current exposure observed ${formatExposureSnapshotTime(exposureUpdatedAt)}.`;
  }
  if (observations !== '') {
    return `${observations}. “Observed” is the actual capture time, which may differ from the listed market time.`;
  }
  return (
    nonEmpty(dataAvailability.exposure.reason) ??
    'Exposure fields need a GEX/VEX snapshot from that session.'
  );
}

function Bias({ bias }: { bias: ExposureBias }) {
  if (bias === 'Unavailable') {
    return <NotAvailable />;
  }
  const className =
    bias === 'Positive' ? 'text-up' : bias === 'Negative' ? 'text-down' : 'text-zinc-500';
  return <span className={className}>{bias}</span>;
}

function NotAvailable() {
  return (
    <span
      className="text-xs text-dim"
      title="No saved exposure observation is available for this row."
    >
      N/A
    </span>
  );
}
